import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ArrowLeft, User, Mail, Lock, Shield, Loader2 } from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { createUser } from '@/api/admin';

// Zod schema for form validation (following user rules)
const createUserSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  name: z.string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters'),
  password: z.string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z.string()
    .min(1, 'Password confirmation is required'),
  role: z.enum(['user', 'admin'], {
    required_error: 'Please select a role',
  }),
  subscriptionStatus: z.enum(['free', 'active', 'inactive', 'cancelled'], {
    required_error: 'Please select a subscription status',
  }).optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

export const UserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: 'user',
      subscriptionStatus: 'free',
    },
  });

  const watchedRole = watch('role');
  const watchedSubscriptionStatus = watch('subscriptionStatus');

  const createUserMutation = useMutation({
    mutationFn: async (data: CreateUserFormData) => {
      return await createUser({
        email: data.email,
        name: data.name,
        password: data.password,
        role: data.role,
        subscription_status: data.subscriptionStatus,
      });
    },
    onSuccess: (response) => {
      toast({
        title: "User created successfully",
        description: `${response.user.name} has been added with ${response.user.role} privileges.`,
      });
      
      // Invalidate and refetch user list
      queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
      
      // Navigate back to user management page
      navigate('/admin/users');
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CreateUserFormData) => {
    createUserMutation.mutate(data);
  };

  const handleCancel = () => {
    if (createUserMutation.isPending) {
      return; // Prevent navigation while creating user
    }
    navigate('/admin/users');
  };

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            onClick={handleCancel}
            className="p-2"
            disabled={createUserMutation.isPending}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create New User</h1>
            <p className="text-gray-600">Add a new user to the system</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              Fill in the details below to create a new user account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  {...register('email')}
                  className={errors.email ? "border-red-500" : ""}
                  disabled={createUserMutation.isPending}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  {...register('name')}
                  className={errors.name ? "border-red-500" : ""}
                  disabled={createUserMutation.isPending}
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter secure password"
                  {...register('password')}
                  className={errors.password ? "border-red-500" : ""}
                  disabled={createUserMutation.isPending}
                />
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password.message}</p>
                )}
                <p className="text-xs text-gray-500">
                  Must be at least 6 characters with uppercase, lowercase, and number
                </p>
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  {...register('confirmPassword')}
                  className={errors.confirmPassword ? "border-red-500" : ""}
                  disabled={createUserMutation.isPending}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  User Role
                </Label>
                <Select 
                  value={watchedRole} 
                  onValueChange={(value) => setValue('role', value as 'user' | 'admin')}
                  disabled={createUserMutation.isPending}
                >
                  <SelectTrigger className={errors.role ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User - Regular user privileges</SelectItem>
                    <SelectItem value="admin">Admin - Full admin privileges</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>

              {/* Subscription Status */}
              <div className="space-y-2">
                <Label>Subscription Status</Label>
                <Select 
                  value={watchedSubscriptionStatus} 
                  onValueChange={(value) => setValue('subscriptionStatus', value as 'free' | 'active' | 'inactive' | 'cancelled')}
                  disabled={createUserMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select subscription status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free - No subscription</SelectItem>
                    <SelectItem value="active">Active - Paid subscription</SelectItem>
                    <SelectItem value="inactive">Inactive - Subscription paused</SelectItem>
                    <SelectItem value="cancelled">Cancelled - Subscription ended</SelectItem>
                  </SelectContent>
                </Select>
                {errors.subscriptionStatus && (
                  <p className="text-sm text-red-600">{errors.subscriptionStatus.message}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={createUserMutation.isPending}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={createUserMutation.isPending}
                  className="min-w-[120px]"
                >
                  {createUserMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default UserCreatePage;
